<template>
  <div class="login-page">
    <div class="ambient-glow"></div>
    <div
      ref="flyer"
      class="logo-flyer"
      :class="{ 'is-landed': hasLanded }"
      :style="flyerStyle"
    >
      <brand-mark animated />
    </div>

    <main v-if="!isLoading" class="auth-column">
      <header class="auth-header">
        <div ref="brandSlot" class="brand-mark"></div>
        <h1>{{ $t('loginPage.login_page_form_title') }}</h1>
        <p>{{ $t('loginPage.login_page_form_subtitle') }}</p>
      </header>

      <form class="auth-form" @submit.prevent="submitLogin">
        <design-input
          v-model:custom-value="username"
          type="text"
          leading-icon="user.svg"
          :hint="$t('generic.username')"
        />

        <design-input
          v-model:custom-value="password"
          type="password"
          leading-icon="lock.svg"
          hide-content
          :hint="$t('generic.password')"
        />

        <design-input
          v-model:custom-value="orgId"
          type="number"
          leading-icon="hash.svg"
          :hint="$t('generic.org_id')"
        />

        <DesignButton
          class="submit-button"
          type="submit"
          full-width
          :hide-label-mobile="false"
          :label="$t('loginPage.login_button')"
          :loading="accountState.isLoadingRequest"
          :disabled="accountState.isLoadingRequest"
        />

        <div class="error-slot">
          <transition name="fade" mode="out-in">
            <p v-if="accountState.loginMessage" class="error-msg">{{ accountState.loginMessage }}</p>
          </transition>
        </div>
      </form>
    </main>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DesignInput from './components/DesignInput.vue';
import DesignButton from './components/DesignButton.vue';
import BrandMark from './components/BrandMark.vue';

// Duree du trace des trois arcs, puis du vol vers le header.
const DRAW_DURATION = 1250;
const FLY_DURATION = 320;

export default {
  name: 'LoginPage',
  components: {
    DesignInput,
    DesignButton,
    BrandMark
  },
  data() {
    return {
      username: '',
      password: '',
      orgId: '',
      isLoading: true,
      hasLanded: false,
      flyerStyle: null,
      timeoutIds: [],
    }
  },
  computed: {
    ...mapState({
      accountState: 'account'
    })
  },
  methods: {
    ...mapActions('account', {
      login: 'login'
    }),
    submitLogin() {
      this.login({
        username: this.username,
        password: this.password,
        orgId: this.orgId
      });
    },
    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    startIntro() {
      if (this.prefersReducedMotion()) {
        this.isLoading = false;
        this.hasLanded = true;
        this.$nextTick(() => this.moveToBrandSlot());
        return;
      }

      this.timeoutIds.push(setTimeout(() => {
        this.isLoading = false;
        this.$nextTick(() => this.flyToBrandSlot());
      }, DRAW_DURATION));
    },
    moveToBrandSlot() {
      const slot = this.$refs.brandSlot;

      if (!this.$refs.flyer || !slot) {
        return;
      }

      const page = this.$el.getBoundingClientRect();
      const to = slot.getBoundingClientRect();

      if (!to.width) {
        return;
      }

      const dx = (to.left + to.width / 2) - (page.left + page.width / 2);
      const dy = (to.top + to.height / 2) - (page.top + page.height / 2);

      this.flyerStyle = {
        width: `${to.width}px`,
        height: `${to.height}px`,
        transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`
      };
    },
    flyToBrandSlot() {
      const flyer = this.$refs.flyer;

      if (!flyer) {
        return;
      }

      const land = () => {
        if (this.hasLanded) {
          return;
        }
        this.hasLanded = true;
      };

      flyer.addEventListener("transitionend", (event) => {
        if (event.propertyName === "transform") {
          land();
        }
      });
      void flyer.offsetWidth;
      this.moveToBrandSlot();

      this.timeoutIds.push(setTimeout(land, FLY_DURATION + 200));
    },
    onResize() {
      this.moveToBrandSlot();
    },
    clearTimeouts() {
      this.timeoutIds.forEach(clearTimeout);
      this.timeoutIds = [];
    }
  },
  mounted() {
    document.getElementById('app')?.classList.add('no-app-padding');
    window.addEventListener('resize', this.onResize);
    this.startIntro();
  },
  beforeUnmount() {
    this.clearTimeouts();
    window.removeEventListener('resize', this.onResize);
    document.getElementById('app')?.classList.remove('no-app-padding');
  }
}
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  background: $color-primary-back;
}

@media(max-width: 768px) {
  .ambient-glow {
    display: none !important
  }
}
.ambient-glow {
  position: absolute;
  top: -30%;
  left: 50%;
  transform: translateX(-50%);
  width: 1000px;
  height: 600px;
  background: radial-gradient(ellipse at center, rgba(131, 147, 244, 0.09), transparent 65%);
  pointer-events: none;
}

.auth-column {
  position: relative;
  z-index: 1;
  width: min(360px, 100%);
  animation: column-intro 420ms ease-out both;
}

@keyframes column-intro {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.auth-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 44px;

  .brand-mark {
    width: 52px;
    height: 52px;
    margin-bottom: 22px;
  }

  h1 {
    margin: 0;
    color: $color-white-text;
    font-size: 26px;
    font-weight: 500;
    letter-spacing: -0.01em;
  }

  p {
    margin: 8px 0 0;
    color: $color-secondary-text;
    font-size: 14px;
  }
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: -20px;
}

.error-slot {
  min-height: 20px;
  margin-top: 0;
}

.error-msg {
  margin: 0;
  color: $color-pastel-red;
  font-size: 13px;
  text-align: center;
}

.submit-button {
  margin-top: 15px;
}

.logo-flyer {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  width: 132px;
  height: 132px;
  transform: translate(-50%, -50%);
  transform-origin: center center;
  transition:
    transform 320ms cubic-bezier(0.65, 0, 0.35, 1),
    width 320ms cubic-bezier(0.65, 0, 0.35, 1),
    height 320ms cubic-bezier(0.65, 0, 0.35, 1) !important;
  pointer-events: none;

  &.is-landed {
    transition: none !important;
  }
}

@media (max-width: $screen-md) {
  .auth-header {
    margin-bottom: 36px;
  }

  .logo-flyer {
    width: 96px;
    height: 96px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-column {
    animation: none;
  }

  .logo-flyer {
    transition: none;
  }
}
</style>
